import { useState, type FormEvent } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormField,
  NotificationCard,
  useToast,
} from '@afrilink/ui';
import { siteConfig } from '@afrilink/config';
import { supabase } from '@/shared/lib/supabase';

/**
 * AddHostingModal — admin-on-behalf-of hosting order.
 *
 * Calls the extended service-order-confirm EF with `acting_for_customer_id`.
 * The result is an invoice the customer pays via PayFast (or staff records
 * manual payment via the existing Billing tab + payment-record-manual EF).
 *
 * Domain modes mirror the customer-side NewServicePage wizard:
 *   - register: bundled .co.za / .org.za / .net.za / .web.za registration
 *   - existing: customer already owns the domain (no registrar call)
 *   - later:    provision under a wildcard FQDN; customer picks later
 */

const PLAN_OPTIONS = [
  { slug: 'entry-monthly', label: 'SightHost Entry (R 79/mo · 10 GB · 50 emails · 5 DBs)' },
  { slug: 'basic-monthly', label: 'SightHost Basic (R 129/mo · 25 GB · 50 emails · 10 DBs)' },
  { slug: 'super-monthly', label: 'SightHost Super (R 289/mo · 10 GB · 100 emails · 100 DBs)' },
  { slug: 'ultimate-monthly', label: 'SightHost Ultimate (R 499/mo · 50 GB · unlimited)' },
];

interface RegistrantInput {
  name: string;
  email: string;
  contactNumber: string;
  address1: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  organisation?: string;
}

interface Props {
  customerId: string;
  customerEmail: string;
  customerName: string;
  onClose: () => void;
  onSubmitted: (result: { invoice_id: string; invoice_number: string; pay_url: string }) => void;
}

export function AddHostingModal({ customerId, customerEmail, customerName, onClose, onSubmitted }: Props) {
  const { toast } = useToast();
  const [planSlug, setPlanSlug] = useState('basic-monthly');
  const [fqdnMode, setFqdnMode] = useState<'register' | 'existing' | 'later'>('register');
  const [registerFqdn, setRegisterFqdn] = useState('');
  const [existingFqdn, setExistingFqdn] = useState('');
  // Defaults: customer's name+email keep them as the legal registrant. Phone
  // and physical address fall back to SightHost's own details (agent-of-record
  // pattern) so admin doesn't have to chase the customer for data ZACR needs.
  // All fields editable — if the customer has their own, just type over.
  const [registrant, setRegistrant] = useState<RegistrantInput>({
    name: customerName,
    email: customerEmail,
    contactNumber: siteConfig.phone,
    address1: siteConfig.address.street,
    city: siteConfig.address.city,
    province: siteConfig.address.region,
    postalCode: siteConfig.address.postalCode,
    country: siteConfig.address.country,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateRegistrant<K extends keyof RegistrantInput>(key: K, value: RegistrantInput[K]) {
    setRegistrant((r) => ({ ...r, [key]: value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Light client-side validation — EF re-validates server-side.
    if (fqdnMode === 'register' && !registerFqdn.trim()) {
      setError('Domain name is required when registering.');
      return;
    }
    if (fqdnMode === 'existing' && !existingFqdn.trim()) {
      setError('Existing domain name is required.');
      return;
    }
    if (fqdnMode === 'register') {
      const missing = (['name', 'email', 'contactNumber', 'address1', 'city', 'province', 'postalCode', 'country'] as const)
        .filter((k) => !registrant[k]?.trim());
      if (missing.length > 0) {
        setError(`Registrant fields missing: ${missing.join(', ')}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const { data, error: efErr } = await supabase.functions.invoke('service-order-confirm', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: {
          plan_slug: planSlug,
          fqdn_mode: fqdnMode,
          register_fqdn: fqdnMode === 'register' ? registerFqdn.trim().toLowerCase() : undefined,
          register_registrant: fqdnMode === 'register' ? registrant : undefined,
          existing_fqdn: fqdnMode === 'existing' ? existingFqdn.trim().toLowerCase() : undefined,
          terms_accepted: true,
          acting_for_customer_id: customerId,
        },
      });
      if (efErr) throw new Error(efErr.message ?? 'Edge function call failed');
      const result = data as {
        ok: true;
        invoice_id: string;
        invoice_number: string;
        pay_url: string;
      };
      toast({
        tone: 'success',
        title: 'Hosting order created',
        body: `Invoice ${result.invoice_number} issued. Customer has been emailed the PayFast link.`,
      });
      onSubmitted(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Add hosting on behalf"
      description={<>Order for <strong>{customerName}</strong> · {customerEmail}</>}
      size="wide"
    >
      <ModalBody>
        <form id="add-hosting-form" onSubmit={onSubmit} className="space-y-4">
          <FormField label="Plan" htmlFor="plan_slug" required>
            <select
              id="plan_slug"
              value={planSlug}
              onChange={(e) => setPlanSlug(e.target.value)}
              disabled={submitting}
              className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {PLAN_OPTIONS.map((p) => (
                <option key={p.slug} value={p.slug}>{p.label}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Domain" htmlFor="fqdn_mode" required>
            <div className="flex flex-wrap gap-2">
              {(['register', 'existing', 'later'] as const).map((m) => (
                <label
                  key={m}
                  className={`flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm ${
                    fqdnMode === m ? 'border-primary bg-primary/10' : 'border-input bg-card'
                  }`}
                >
                  <input
                    type="radio"
                    name="fqdn_mode"
                    value={m}
                    checked={fqdnMode === m}
                    onChange={() => setFqdnMode(m)}
                    disabled={submitting}
                  />
                  {m === 'register' ? 'Register new (.co.za FREE 1st year)' : m === 'existing' ? 'Customer already owns it' : 'Decide later'}
                </label>
              ))}
            </div>
          </FormField>

          {fqdnMode === 'register' && (
            <>
              <FormField label="Domain to register" htmlFor="register_fqdn" required helper=".co.za / .org.za / .net.za / .web.za">
                <Input
                  id="register_fqdn"
                  value={registerFqdn}
                  onChange={(e) => setRegisterFqdn(e.target.value)}
                  disabled={submitting}
                  placeholder="example.co.za"
                  autoComplete="off"
                />
              </FormField>

              <div className="rounded border border-border bg-muted/40 p-4">
                <h4 className="text-sm font-semibold text-foreground">Registrant (ZACR requirement)</h4>
                <p className="mt-1 mb-3 text-xs text-muted-foreground">
                  Customer name + email stay as the legal registrant (they own the domain).
                  Phone + address default to SightHost as agent of record — override below if the customer has their own.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField label="Name" htmlFor="r_name" required>
                    <Input id="r_name" value={registrant.name} onChange={(e) => updateRegistrant('name', e.target.value)} disabled={submitting} />
                  </FormField>
                  <FormField label="Email" htmlFor="r_email" required>
                    <Input id="r_email" type="email" value={registrant.email} onChange={(e) => updateRegistrant('email', e.target.value)} disabled={submitting} />
                  </FormField>
                  <FormField label="Phone" htmlFor="r_phone" required helper="+27.123456789 format">
                    <Input id="r_phone" value={registrant.contactNumber} onChange={(e) => updateRegistrant('contactNumber', e.target.value)} disabled={submitting} placeholder="+27.821234567" />
                  </FormField>
                  <FormField label="Organisation (optional)" htmlFor="r_org">
                    <Input id="r_org" value={registrant.organisation ?? ''} onChange={(e) => updateRegistrant('organisation', e.target.value)} disabled={submitting} />
                  </FormField>
                  <FormField label="Address line 1" htmlFor="r_addr1" required>
                    <Input id="r_addr1" value={registrant.address1} onChange={(e) => updateRegistrant('address1', e.target.value)} disabled={submitting} />
                  </FormField>
                  <FormField label="City" htmlFor="r_city" required>
                    <Input id="r_city" value={registrant.city} onChange={(e) => updateRegistrant('city', e.target.value)} disabled={submitting} />
                  </FormField>
                  <FormField label="Province" htmlFor="r_province" required>
                    <Input id="r_province" value={registrant.province} onChange={(e) => updateRegistrant('province', e.target.value)} disabled={submitting} />
                  </FormField>
                  <FormField label="Postal code" htmlFor="r_postal" required>
                    <Input id="r_postal" value={registrant.postalCode} onChange={(e) => updateRegistrant('postalCode', e.target.value)} disabled={submitting} />
                  </FormField>
                  <FormField label="Country" htmlFor="r_country" required helper="ISO alpha-2 (ZA)">
                    <Input id="r_country" value={registrant.country} onChange={(e) => updateRegistrant('country', e.target.value.toUpperCase().slice(0, 2))} disabled={submitting} maxLength={2} />
                  </FormField>
                </div>
              </div>
            </>
          )}

          {fqdnMode === 'existing' && (
            <FormField label="Customer's existing domain" htmlFor="existing_fqdn" required>
              <Input
                id="existing_fqdn"
                value={existingFqdn}
                onChange={(e) => setExistingFqdn(e.target.value)}
                disabled={submitting}
                placeholder="example.com"
                autoComplete="off"
              />
            </FormField>
          )}

          {error && <NotificationCard tone="danger" title="Could not create order" body={error} />}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="submit" form="add-hosting-form" loading={submitting} disabled={submitting}>
          {submitting ? 'Creating…' : 'Issue invoice'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
